// Shared card-grid layouts used by both the public shop page's category
// sections (ShopPublicView) and the Home tab's Featured Items/Services
// sections (HomePageTemplate) — so both surfaces render the exact same
// 1×/2×/3× card display instead of drifting apart over time.
import styled, { css } from 'styled-components';

// A swipe should always land on the start of a new full "page" of items (one
// screen's worth: N visible columns × rows), not drift to a partial reveal or
// advance just one column at a time. Since every item is its own grid-auto
// column (grid-auto-flow: column), only every Nth item — where N = visible
// columns × rows — should be a snap point; the rest opt out of snapping so
// the browser skips straight past them to the next group.
const snapEveryNCols = (cols, rows) => css`
  scroll-snap-type: x mandatory;
  > * {
    scroll-snap-align: none;
  }
  > *:nth-child(${Math.max(1, cols * rows)}n + 1) {
    scroll-snap-align: start;
  }
`;

// 2×/3× layout: horizontally-scrollable card grid, N columns visible at once
export const CategoryScrollableGrid = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(${props => props.$rows || 1}, auto);
  grid-auto-columns: calc(25% - 0.75rem);
  gap: 1rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 0.5rem;
  margin-bottom: 2rem;
  -webkit-overflow-scrolling: touch;
  align-items: stretch;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => `${props.theme?.colors?.background || '#000000'}80`};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 10px;
  }

  ${props => props.$desktopCols ? css`
    /* 1x on desktop: fixed N-per-row wall-to-wall, ignoring the width-tiered
       columns below — extra items beyond N×rows scroll horizontally. */
    @media (min-width: 769px) {
      grid-auto-columns: calc(${100 / props.$desktopCols}% - 0.67rem);
      grid-template-rows: repeat(${props.$desktopRows || 1}, auto);
      ${snapEveryNCols(props.$desktopCols, props.$desktopRows || 1)}
    }
  ` : css`
    /* Snap grouping below is scoped to mutually-exclusive min+max ranges
       (rather than the overlapping max-width-only ranges grid-auto-columns
       uses) — nth-child selectors are more specific than a plain > * reset,
       so overlapping ranges would let the wrong tier's pattern win by
       specificity instead of by "most recent/narrowest tier", producing a
       snap group size that doesn't match the actually-visible column count. */
    grid-auto-columns: calc(25% - 0.75rem);
    @media (min-width: 901px) {
      ${snapEveryNCols(4, props.$desktopRows || 1)}
    }

    @media (max-width: 900px) {
      grid-auto-columns: calc(33.33% - 0.67rem);
    }
    @media (min-width: 601px) and (max-width: 900px) {
      ${snapEveryNCols(3, props.$desktopRows || 1)}
    }

    @media (min-width: 769px) {
      grid-template-rows: repeat(${props.$desktopRows || 1}, auto);
    }
  `}

  @media (max-width: 600px) {
    grid-auto-columns: ${props => `calc(${100 / (props.$mobileCols || 3)}% - 0.5rem)`};
    gap: 0.5rem;
    ${props => snapEveryNCols(props.$mobileCols || 3, props.$rows || 1)}
  }

  > * {
    /* NOT scroll-snap-align here — each tier above sets it per-item via
       snapEveryNCols so only every Nth item is a snap point. A blanket rule
       here would have identical specificity and, being declared after all
       the tiered overrides, silently win and re-enable snapping on every
       single item everywhere, undoing the whole "snap by page" behavior. */
    min-width: 0;
  }
`;

// 1× hero layout: full-screen card carousel — one card fills the view, scroll-snap to next
export const HeroScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  /* Without this, the browser implicitly treats overflow-y as auto (since
     overflow-x isn't visible) — any sub-pixel vertical overflow (the image's
     zoom-pulse animation, or align-items:stretch sizing across cards of
     slightly different heights) then makes the whole carousel briefly
     vertically draggable instead of staying a pure horizontal swipe. */
  overflow-y: hidden;
  gap: 0;
  padding: 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  margin-bottom: 2rem;
  align-items: stretch;
  &::-webkit-scrollbar { display: none; }

  /* Each card occupies exactly 100% — no adjacent card ever peeks.
     Internal padding creates the side breathing room. */
  > * {
    flex: 0 0 100%;
    scroll-snap-align: start;
    padding: 0 0.75rem;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    > * {
      flex: 0 0 50%;
      padding: 0 0.5rem;
    }
  }

  @media (min-width: 1024px) {
    > * { flex: 0 0 33.33%; }
  }
`;

// Small "N×" grid-density toggle chip — mobile-only quick access (desktop uses the header toggle)
export const GridToggleBtn = styled.button`
  background: ${props => props.$active ? props.theme?.colors?.accent || '#800000' : 'transparent'};
  border: 1px solid ${props => `${props.theme?.colors?.accent || '#800000'}60`};
  border-radius: 10px;
  padding: 0.35rem 0.45rem;
  color: ${props => props.$active ? '#fff' : `${props.theme?.colors?.text || '#fff'}80`};
  font-size: 0.65rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  white-space: nowrap;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:active { transform: scale(0.95); }
  svg { width: 11px; height: 11px; }
  @media (min-width: 768px) { display: none; }
`;
